/**
 * Auto-blocking — defer third-party scripts until matching consent is granted.
 *
 * The site owner replaces their normal:
 *   <script src="https://example.com/analytics.js"></script>
 * with:
 *   <script type="text/plain" data-csteer-category="analytics"
 *           src="https://example.com/analytics.js"></script>
 *
 * Setting `type="text/plain"` tells the browser to NOT parse/execute the
 * script automatically. This SDK scans the document on init and, when
 * consent is granted for a category, swaps the deferred scripts into a
 * fresh executable <script> tag in DOM order.
 *
 * The pattern also supports inline scripts:
 *   <script type="text/plain" data-csteer-category="marketing">
 *     gtag('event', 'pageview');
 *   </script>
 *
 * Why not just flip `type` to `text/javascript`? Because the browser only
 * fetches/executes a script when the tag is INSERTED into the DOM with an
 * executable type. Mutating an existing `type` attribute is a no-op. We
 * therefore clone the deferred tag into a new script element and replace
 * the placeholder.
 *
 * Each handled element gets a `data-csteer-activated="true"` marker so
 * we don't re-activate it on subsequent calls (e.g. if consent is
 * granted, then revoked, then granted again).
 */

import type { ConsentChoices } from "./types.js";

const SELECTOR = 'script[type="text/plain"][data-csteer-category]';
const ACTIVATED_ATTR = "data-csteer-activated";

export function activateBlockedScripts(choices: ConsentChoices): void {
  if (typeof document === "undefined") return;
  const placeholders = document.querySelectorAll<HTMLScriptElement>(SELECTOR);
  for (const placeholder of placeholders) {
    if (placeholder.getAttribute(ACTIVATED_ATTR) === "true") continue;
    const category = placeholder.dataset.csteerCategory as keyof ConsentChoices | undefined;
    if (!category) continue;
    if (choices[category] !== true) continue;

    const script = document.createElement("script");
    // Preserve attributes (src, async, defer, integrity, crossorigin, nonce,
    // referrerpolicy, type=module if requested). We do NOT preserve the
    // data-csteer-category attribute (no longer needed) nor `type=text/plain`
    // (we want execution this time).
    for (const attr of placeholder.attributes) {
      if (attr.name === "type") continue;
      if (attr.name === "data-csteer-category") continue;
      script.setAttribute(attr.name, attr.value);
    }
    // Honor an explicit `data-csteer-type` override for module / nomodule
    // (e.g. data-csteer-type="module" → final type=module).
    const overrideType = placeholder.dataset.csteerType;
    if (overrideType) script.setAttribute("type", overrideType);

    if (!placeholder.src) {
      // Inline script — copy the body.
      script.textContent = placeholder.textContent ?? "";
    }

    placeholder.setAttribute(ACTIVATED_ATTR, "true");
    placeholder.parentNode?.insertBefore(script, placeholder.nextSibling);
  }
}
